<template>
  <header>
    <h1 @click="toggleAuthModal">PTO Tracker</h1>
    <ListContainer>
      <Badge
        title="Entitled"
        v-model="data.entitledHours"
        @toggleModal="toggleColumnUpdateModal"
      />
      <Badge
        title="Bought"
        v-model="data.boughtHours"
        @toggleModal="toggleColumnUpdateModal"
      />
      <Badge
        title="Carried"
        v-model="data.carriedHours"
        @toggleModal="toggleColumnUpdateModal"
      />
      <Badge
        title="Total Starting"
        v-model="data.startingHours"
        :editable="false"
      />
    </ListContainer>
    <ListContainer>
      <Badge title="Scheduled" v-model="scheduledHours" :editable="false" />
      <Badge title="Taken" v-model="takenHours" :editable="false" />
      <Badge
        title="Total Remaining"
        v-model="remainingHours"
        :editable="false"
      />
    </ListContainer>
  </header>

  <main>
    <Table
      v-if="tableData != {}"
      :headers="tableHeaders"
      :data="tableData"
      @toggleModal="toggleModal"
    >
    </Table>
    <Modal
      v-if="showModal"
      @toggleModal="toggleModal"
      title="Update Row"
      id="updateRowModal"
    >
      <template v-slot:body>
        <h4>{{ new Date(rowUpdateData.date).toDateString() }}</h4>
        <TextInput title="Hours" v-model="rowUpdateData.hours" />
        <TextInput type="text" title="Notes" v-model="rowUpdateData.note" />
      </template>

      <template v-slot:footer>
        <button @click="deletePto">Delete</button>
        <button @click="toggleModal">Cancel</button>
        <button @click="updatePto">Update</button>
      </template>
    </Modal>
    <Modal
      v-if="showColumnModal"
      @toggleModal="toggleColumnUpdateModal"
      title="Update Value"
      id="updateColumnModal"
    >
      <template v-slot:body>
        <TextInput
          type="text"
          :title="columnUpdateData.title"
          v-model="columnUpdateData.value"
        />
      </template>

      <template v-slot:footer>
        <button @click="toggleColumnUpdateModal">Cancel</button>
        <button @click="updateColumn">Update</button>
      </template>
    </Modal>
    <Modal
      v-if="showAuthModal"
      @toggleModal="toggleAuthModal"
      title="Enter Password"
      id="authColumnModal"
    >
      <template v-slot:body>
        <TextInput type="text" title="Password" v-model="userPassword" />
      </template>

      <template v-slot:footer>
        <button
          @click="
            {
              this.userPassword = '';
              toggleAuthModal();
            }
          "
        >
          Cancel
        </button>
        <button @click="toggleAuthModal">Update</button>
      </template>
    </Modal>
  </main>
</template>

<script>
import { toRaw } from "vue";
import * as yearsController from "../api/yearsController";
import * as ptoController from "../api/ptoController";
import FlexContainer from "../components/FlexContainer.vue";
import TextInput from "../components/TextInput.vue";
import ListContainer from "../components/ListContainer.vue";
import Table from "../components/Table.vue";
import Modal from "../components/modal/Modal.vue";
import NotificationBanner from "../components/NotificationBanner.vue";
import Badge from "@/components/Badge.vue";

export default {
  components: {
    FlexContainer,
    TextInput,
    ListContainer,
    Table,
    Modal,
    NotificationBanner,
    Badge,
  },

  data() {
    return {
      selectedYear: 2023,
      data: {},
      scheduledHours: 0,
      takenHours: 0,
      remainingHours: 0,
      tableData: [],
      tableHeaders: [
        "Week Number",
        "Commencing Date",
        "PTO Hours",
        "Remaining Balance",
        "Notes",
      ],
      rowUpdateData: {
        date: null,
        hours: 0,
        note: "",
      },
      showModal: false,
      columnUpdateData: {
        value: 0,
        title: "",
      },
      showColumnModal: false,
      userPassword: "",
      showAuthModal: false,
    };
  },

  methods: {
    async getData() {
      const rspData = await yearsController.GetYear(this.selectedYear);

      let data = {
        year: rspData[0].year,
        firstMonday: rspData[0].first_monday,
        entitledHours: rspData[0].entitled_hours,
        boughtHours: rspData[0].bought_hours,
        carriedHours: rspData[0].carried_hours,
        startingHours: rspData[0].entitled_hours + rspData[0].carried_hours,
        scheduledPto:
          rspData[0].scheduled_pto != null
            ? JSON.parse(rspData[0].scheduled_pto)
            : [],
      };

      this.data = data;
      return data;
    },
    async updatePto() {
      let date = new Date(this.rowUpdateData.date);
      date = date.toISOString().split("T")[0];

      const hours = this.verifyValue(this.rowUpdateData.hours);

      //check if existing in db
      const existing = await ptoController.getPtoByDate(
        this.userPassword,
        date
      );
      let rspData;

      if (Array.isArray(existing) && existing.length == 0) {
        //if not existing then insert new row
        rspData = await ptoController.insertPtoRow(this.userPassword, {
          date: date,
          hours: hours,
          note: this.rowUpdateData.note,
        });

        this.data.scheduledPto.push({
          date: date,
          hours: hours,
          note: this.rowUpdateData.note,
        });
      } else {
        //if existing then update existing row
        rspData = await ptoController.updatePto(this.userPassword, {
          date: date,
          hours: hours,
          note: this.rowUpdateData.note,
        });

        const index = this.data.scheduledPto.map((t) => t.date).indexOf(date);

        this.data.scheduledPto[index] = {
          date: date,
          hours: hours,
          note: this.rowUpdateData.note,
        };
      }

      if (!rspData) return;

      this.toggleModal();
      this.displayData();
    },
    async deletePto() {
      let date = new Date(this.rowUpdateData.date);
      date = date.toISOString().split("T")[0];
      const existing = await ptoController.getPtoByDate(
        this.userPassword,
        date
      );

      if (!this.data.scheduledPto.some((t) => t.date == date)) return;
      if (Array.isArray(existing) && existing.length == 0) return;

      const index = this.data.scheduledPto.map((t) => t.date).indexOf(date);
      this.data.scheduledPto.splice(index, 1);

      const rspData = await ptoController.deletePtoRow(this.userPassword, date);

      if (!rspData) return;

      this.toggleModal();
      this.displayData();
    },
    updateColumn() {
      const columnName = this.columnUpdateData.title
        .toLowerCase()
        .replace(" ", "_");

      const varName =
        this.columnUpdateData.title.charAt(0).toLowerCase() +
        this.columnUpdateData.title.slice(1).replace(" ", "");

      const val = this.verifyValue(this.columnUpdateData.value);

      this.data[varName] = val;

      yearsController.updateYear(
        this.userPassword,
        this.selectedYear,
        columnName,
        val
      );

      this.toggleColumnUpdateModal();
      this.displayData();
    },

    getTableRows() {
      let remainingHours = this.data.startingHours;
      let rows = [];
      const allDates = this.getAllDatesInYear(this.data);

      allDates.forEach((date, index) => {
        const week = this.getWeekNumberFromDate(date);
        const pto = this.getPtoFromDate(date);
        remainingHours -= pto ? pto.hours : 0;

        //Add this years bought hours if first week in Sep
        if (date.getMonth() == 8 && allDates[index - 1].getMonth() == 7) {
          remainingHours += this.data.boughtHours;
        }

        const row = {
          week: week,
          date: date,
          ptoHours: pto ? pto.hours : 0,
          remainingHours: remainingHours,
          notes: pto ? pto.note : "",
        };

        rows.push(row);
      });

      this.remainingHours = remainingHours;
      this.tableData = rows;
    },
    getAllDatesInYear(data) {
      let dates = [];
      let thisYear = data.year;
      let date = new Date(data.firstMonday);

      while (date.getFullYear() == thisYear) {
        dates.push(new Date(date));
        date.setDate(date.getDate() + 7);
      }

      return dates;
    },
    getWeekNumberFromDate(date) {
      var today = new Date(date);
      const start = new Date(today.getFullYear(), 0, 1);

      var days = Math.floor((today - start) / (24 * 60 * 60 * 1000));

      var weekNumber = Math.ceil(days / 7);
      return weekNumber + 1;
    },
    getPtoFromDate(date) {
      if (
        this.data.scheduledPto?.some((t) => {
          const ptoTime = new Date(t.date).toISOString().split("T")[0];
          const todayTime = new Date(date).toISOString().split("T")[0];

          return ptoTime == todayTime;
        })
      ) {
        const index = this.data.scheduledPto
          .map((t) => new Date(t.date).toISOString().split("T")[0])
          .indexOf(new Date(date).toISOString().split("T")[0]);

        return toRaw(this.data.scheduledPto[index]);
      }
    },
    toggleModal(row) {
      if (!this.verifyPass()) {
        this.toggleAuthModal();
        return;
      }

      this.showModal = !this.showModal;
      if (!this.showModal) return;

      row = toRaw(row);
      this.rowUpdateData.date = row.date;
      this.rowUpdateData.hours = row.ptoHours;
      this.rowUpdateData.note = row.notes;
    },
    toggleColumnUpdateModal(value, title) {
      if (!this.verifyPass()) {
        this.toggleAuthModal();
        return;
      }

      this.showColumnModal = !this.showColumnModal;

      if (!this.showColumnModal) {
        this.data.startingHours =
          this.verifyValue(this.data.entitledHours) +
          this.verifyValue(this.data.carriedHours);

        return;
      }

      this.columnUpdateData.value = value;
      this.columnUpdateData.title = title;
    },
    toggleAuthModal() {
      this.showAuthModal = !this.showAuthModal;
    },
    getScheduledHours() {
      let hours = 0;
      if (!this.data.scheduledPto) return hours;

      this.data.scheduledPto.forEach((s) => {
        const scheduledDate = new Date(s.date);
        const today = new Date();

        if (scheduledDate > today) hours += s.hours;
      });

      this.scheduledHours = hours;
    },
    getTakenHours() {
      let hours = 0;
      if (!this.data.scheduledPto) return hours;

      this.data.scheduledPto.forEach((s) => {
        const scheduledDate = new Date(s.date);
        const today = new Date();

        if (scheduledDate < today) hours += s.hours;
      });

      this.takenHours = hours;
    },
    verifyValue(value) {
      return !value ? 0 : parseFloat(value);
    },
    verifyPass() {
      return (
        this.userPassword != "" &&
        this.userPassword == process.env.VUE_APP_BEARER_TOKEN
      );
    },
    displayData() {
      this.getTableRows();
      this.getScheduledHours();
      this.getTakenHours();
    },
  },

  async mounted() {
    await this.getData();
    this.displayData();
  },
};
</script>
